import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Loader2, Calendar, User, CreditCard, 
  Check, X, Clock, RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../AuthContext';
import { adminApi } from '../../services/api';
import { supabase } from '../../supabaseClient';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const AdminBookingsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [updating, setUpdating] = useState(null);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      
      if (!token) {
        navigate('/login');
        return;
      }
      
      const status = statusFilter === 'all' ? null : statusFilter;
      const data = await adminApi.getBookings(token, status);
      setBookings(data);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      if (err.message.includes('403')) {
        navigate('/admin');
      }
      toast({
        title: "Error",
        description: err.message || "Failed to load bookings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, navigate, toast]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchBookings();
  }, [isAuthenticated, navigate, fetchBookings]);

  const handleStatusChange = async (bookingId, newStatus, paymentStatus = null) => {
    try {
      setUpdating(bookingId);
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      
      await adminApi.updateBookingStatus(bookingId, newStatus, paymentStatus, token);
      
      toast({
        title: "Success",
        description: `Booking status updated to ${newStatus}`,
      });
      
      fetchBookings();
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to update booking",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#22c55e';
      case 'pending': return '#eab308';
      case 'cancelled': return '#ef4444';
      case 'completed': return '#3b82f6';
      default: return 'var(--text-secondary)';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return '#22c55e';
      case 'pending': return '#eab308';
      case 'refunded': return '#ef4444';
      default: return 'var(--text-secondary)';
    }
  };

  const getCurrencySymbol = (curr) => {
    const symbols = { USD: '$', ZAR: 'R', GBP: '£', EUR: '€' };
    return symbols[curr] || '$';
  };

  if (!isAuthenticated) return null;

  return (
    <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }} data-testid="admin-bookings">
      <div className="container py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="outline" size="icon" style={{ borderRadius: 0 }}>
              <ArrowLeft size={20} />
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="hero-medium">Manage Bookings</h1>
          </div>
          <Button variant="outline" onClick={fetchBookings} style={{ borderRadius: 0 }}>
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-8">
          <div>
            <label className="body-small block mb-2" style={{ color: 'var(--text-secondary)' }}>
              Status Filter
            </label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" style={{ borderRadius: 0 }} data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Bookings</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Bookings List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin" size={32} style={{ color: 'var(--olive-primary)' }} />
          </div>
        ) : bookings.length === 0 ? (
          <div className="p-12 text-center" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              No bookings found
            </p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="bookings-list">
            {bookings.map((booking) => (
              <div
                key={booking.id}
                className="p-6"
                style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}
                data-testid={`booking-${booking.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <h3 className="heading-3">{booking.booking_reference}</h3>
                      <span
                        className="px-3 py-1 text-xs uppercase"
                        style={{ backgroundColor: getStatusColor(booking.status), color: 'white' }}
                      >
                        {booking.status}
                      </span>
                      <span
                        className="px-3 py-1 text-xs uppercase"
                        style={{ backgroundColor: getPaymentStatusColor(booking.payment_status), color: 'white' }}
                      >
                        {booking.payment_status}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="flex items-center gap-2">
                        <User size={16} style={{ color: 'var(--text-secondary)' }} />
                        <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          {booking.guest_first_name} {booking.guest_last_name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar size={16} style={{ color: 'var(--text-secondary)' }} />
                        <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          {new Date(booking.check_in).toLocaleDateString()} - {new Date(booking.check_out).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CreditCard size={16} style={{ color: 'var(--text-secondary)' }} />
                        <span className="body-regular font-semibold">
                          {getCurrencySymbol(booking.currency)}{booking.total?.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    <p className="body-small mt-2" style={{ color: 'var(--text-light)' }}>
                      Room: {booking.room_name} | Email: {booking.guest_email}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    {booking.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          className="btn-primary flex items-center gap-1"
                          onClick={() => handleStatusChange(booking.id, 'confirmed', 'paid')}
                          disabled={updating === booking.id}
                          data-testid={`confirm-${booking.id}`}
                        >
                          {updating === booking.id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Check size={14} />
                          )}
                          Confirm
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex items-center gap-1"
                          onClick={() => handleStatusChange(booking.id, 'cancelled')}
                          disabled={updating === booking.id}
                          style={{ borderRadius: 0, borderColor: '#ef4444', color: '#ef4444' }}
                          data-testid={`cancel-${booking.id}`}
                        >
                          <X size={14} />
                          Cancel
                        </Button>
                      </>
                    )}
                    {booking.status === 'confirmed' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex items-center gap-1"
                        onClick={() => handleStatusChange(booking.id, 'completed')}
                        disabled={updating === booking.id}
                        style={{ borderRadius: 0 }}
                        data-testid={`complete-${booking.id}`}
                      >
                        {updating === booking.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Check size={14} />
                        )}
                        Mark Complete
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBookingsPage;
